import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  alpha,
  Box,
  Chip,
  chipClasses,
  Grow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useFormCustomContext } from '../index';
import DownIcon from '@mui/icons-material/ArrowDropDownOutlined';
import XIcon from '@mui/icons-material/CloseOutlined';
import { formatNumber } from 'src/viewsUtils';

export interface BaseChipProps {
  name: string;
  formattedSelectedValue: string | string[] | null;
  isOpen: boolean;
  resetValue: () => void;
  label: ReactNode;
  disabled?: boolean;
  tooltipTitle: string | ReactNode;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
  children?: any;
}

const CHIP_LABEL_MAX_WIDTH = 450;

export const BaseChip: React.FC<BaseChipProps> = ({
  isOpen,
  name,
  label,
  formattedSelectedValue: value,
  children = null,
  disabled,
  tooltipTitle = '',
  resetValue,
  onClick,
}) => {
  const chipRef = useRef<HTMLElement>(null);
  const [tooltip, setTooltip] = useState<string | ReactNode>('');
  const { submittedFields } = useFormCustomContext();
  const isWithValue = useMemo(
    () =>
      (Array.isArray(value) && value?.length > 0) ||
      (typeof value === 'string' && !!value),
    [value],
  );

  const isDraft = useMemo(
    () =>
      isWithValue &&
      submittedFields?.length > 0 &&
      submittedFields.includes(name),
    [isWithValue, submittedFields, name],
  );

  const selectedValueIsMultiple = useMemo(
    () => Array.isArray(value) && value?.length > 1,
    [value],
  );

  const getSingleValueToString = useCallback(() => {
    if (!selectedValueIsMultiple) {
      if (Array.isArray(value) && value?.length === 1) {
        return value[0].toString();
      }
      if (typeof value === 'string' && !!value) {
        return value;
      }
    }
  }, [selectedValueIsMultiple, value]);

  useEffect(() => {
    if (tooltipTitle) {
      setTooltip(tooltipTitle);
    }
  }, [tooltipTitle]);

  useEffect(() => {
    if (chipRef.current) {
      const width = chipRef.current?.offsetWidth;

      if (width > CHIP_LABEL_MAX_WIDTH) {
        setTooltip(getSingleValueToString());
      }
    }
  }, [getSingleValueToString]);

  return (
    <Tooltip title={isOpen ? '' : tooltip}>
      <Box ref={chipRef}>
        <Chip
          disabled={disabled}
          variant="outlined"
          onClick={onClick}
          sx={(theme) => ({
            borderColor: isWithValue
              ? theme.palette.primary.main
              : theme.palette.dividerLight,
            backgroundColor: isDraft
              ? alpha(theme.palette.primary.light, 0.15)
              : theme.palette.background.paper,
            color: isWithValue ? theme.palette.primary.main : undefined,
            // opacity: 0.85,
            lineHeight: 'normal',
            minWidth: 'unset',
            pl: 2,
            pr: 0.5,
            '&:hover': {
              backgroundColor: isDraft
                ? alpha(theme.palette.primary.light, 0.2)
                : theme.palette.dividerLight,
            },
            [`& .${chipClasses.label}`]: {
              display: 'block',
              pl: 0,
              pr: 1,
              maxWidth: CHIP_LABEL_MAX_WIDTH,
            },
            [`& .${chipClasses.deleteIcon}`]: {
              color: theme.palette.action.active,
              opacity: 0.85,
            },
          })}
          onDelete={isWithValue ? resetValue : () => {}}
          label={
            label ? (
              <Box display="flex" gap={0.5}>
                <Box component="span">
                  <Typography fontWeight={400} variant="caption">
                    {`${label}${isWithValue ? ':' : ''}`}
                  </Typography>
                </Box>
                <Grow
                  in={isWithValue}
                  style={{
                    transformOrigin: 'center left',
                  }}
                >
                  <Typography variant="caption" fontWeight={500} noWrap>
                    {selectedValueIsMultiple
                      ? `${formatNumber(value?.length ?? 0)} נבחרו`
                      : value}
                  </Typography>
                </Grow>
              </Box>
            ) : null
          }
          deleteIcon={
            isWithValue ? (
              <XIcon
                sx={{
                  fontSize: '1.125rem !important',
                  transform: `rotateZ(180deg)`,
                  margin: ({ spacing }) =>
                    `${spacing(0, 0.625, 0, -0.75)} !important`,
                }}
              />
            ) : (
              <DownIcon
                sx={{
                  fontSize: '1.125rem !important',
                  pointerEvents: 'none',
                  transition: '0.3s cubic-bezier(.03,.98,.52,.99)',
                  transform: `rotateZ(${isOpen ? '180' : '0'}deg)`,
                  margin: ({ spacing }) =>
                    `${spacing(0, 0.625, 0, -0.75)} !important`,
                }}
              />
            )
          }
        />
        {children}
      </Box>
    </Tooltip>
  );
};
